import { BAR_FILL_FADE_FROM, FillBar, FormFieldLabel } from '@alison/ui';
import Box from '@mui/material/Box';
import { alpha, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useId } from 'react';

const MAX_SCORE = 10;
const GOOD_ABOVE = 8;
const FAIR_FROM = 5;
const RING_STROKE_RATIO = 10 / 168;
const SCORE_DIGITS = 1;

export const LABEL_SX = { fontSize: 11, letterSpacing: '0.04em' };

export function readable(score: number | null): string {
  return score === null ? '—' : score.toFixed(SCORE_DIGITS);
}

export type ScoreBand = 'low' | 'medium' | 'high';

// Banded on the shown value, so a 6.96 that reads 7.0 is coloured as 7.0.
export function bandOf(score: number | null): ScoreBand {
  if (score === null) return 'low';
  const shown = Number(score.toFixed(SCORE_DIGITS));
  if (shown < FAIR_FROM) return 'low';
  return shown > GOOD_ABOVE ? 'high' : 'medium';
}

export function toneOf(theme: Theme, score: number | null): string {
  if (score === null) return theme.palette.text.disabled;
  if (score > GOOD_ABOVE) return theme.palette.score.high.main;
  return score >= FAIR_FROM ? theme.palette.score.medium.main : theme.palette.score.low.main;
}

export function ScorePip({ score }: { score: number | null }): ReactNode {
  const theme = useTheme();
  const tone = toneOf(theme, score);
  return (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: '50%',
        border: '2px solid',
        borderColor: tone,
        color: tone,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {score === null ? '—' : score.toFixed(1)}
    </Box>
  );
}

export function ScoreRing({
  score,
  size,
  caption = 'Overall score',
}: {
  score: number | null;
  size: number;
  caption?: string;
}): ReactNode {
  const theme = useTheme();
  const fadeId = useId();
  const tone = toneOf(theme, score);
  const stroke = size * RING_STROKE_RATIO;
  const centre = size / 2;
  const radius = centre - stroke / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = score === null ? 0 : Math.min(score / MAX_SCORE, 1);

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} role="img" aria-label={caption}>
        <defs>
          <linearGradient id={fadeId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={alpha(tone, BAR_FILL_FADE_FROM)} />
            <stop offset="100%" stopColor={tone} />
          </linearGradient>
        </defs>
        <circle
          cx={centre}
          cy={centre}
          r={radius}
          fill="none"
          stroke={theme.palette.divider}
          strokeWidth={stroke}
        />
        <circle
          cx={centre}
          cy={centre}
          r={radius}
          fill="none"
          stroke={`url(#${fadeId})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${circumference * filled} ${circumference}`}
          transform={`rotate(-90 ${centre} ${centre})`}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: size * 0.23, fontWeight: 500, lineHeight: 1.1 }}>
          {readable(score)}
        </Typography>
        <Typography sx={{ fontSize: size * 0.075, color: 'text.muted' }}>{caption}</Typography>
      </Box>
    </Box>
  );
}

export function AttributeBar({
  name,
  percent,
  labelWidth = 96,
}: {
  name: string;
  percent: number;
  labelWidth?: number;
}): ReactNode {
  const { palette } = useTheme();
  const tone = palette.elementCategory[name] ?? palette.elementCategory.default;

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <FormFieldLabel sx={{ ...LABEL_SX, width: labelWidth, flexShrink: 0, color: tone }}>
        {name.toUpperCase()}
      </FormFieldLabel>
      <FillBar color={tone} percent={percent} label={name} height={7} sx={{ flex: 1 }} />
      <Typography sx={{ fontSize: 14, width: 44, textAlign: 'right', flexShrink: 0, color: tone }}>
        {Math.round(percent)}%
      </Typography>
    </Box>
  );
}
